/**
 * Card attachments.
 *
 * ## The size check happens twice, on purpose
 *
 * The client checks `file.size` against `MAX_UPLOAD_BYTES` before sending, which
 * is a courtesy: it turns a five-megabyte upload that would be rejected into an
 * instant local message. The server checks the declared `Content-Length` *and* the
 * real byte length afterwards, because the client's check is not a control - it is
 * a user experience. `apps/api/src/routes/attachments.ts` documents the same
 * ordering from the other side.
 *
 * ## Why SVG is not accepted
 *
 * `sniffContentType` in `packages/shared/src/media.ts` identifies files by their
 * magic bytes rather than by the declared type, and deliberately does not recognise
 * `image/svg+xml`. An SVG is a document that can carry `<script>`, and serving one
 * from the same origin as the application turns a "file upload" into stored XSS.
 * An SVG uploaded here is stored as `text/plain` and renders as text. That is a
 * real limitation and it is stated rather than hidden.
 *
 * ## Why the bytes are fetched from an unguessable URL rather than a signed one
 *
 * `GET /api/media/:key` is public and the key is the capability. The alternative -
 * a short-lived signed URL - needs a signing key, a clock the client and server
 * agree on, and a renewal path for an image that is still on screen when its
 * signature expires. An unguessable key with the board's authorisation checked at
 * upload time is a smaller surface and has no expiry failure mode.
 */

import { useRef, useState, type ReactNode } from 'react';
import { MAX_UPLOAD_BYTES } from '@slate/shared';
import { mediaUrl } from '../api/client.js';
import { useBoard } from '../state/BoardProvider.js';
import { Icon, ghostButtonClass, useTick } from './ui.js';
import { formatBytes, relativeTime } from '../lib/format.js';

export function AttachmentPanel({ cardId }: { cardId: string }): ReactNode {
  const { state, actions } = useBoard();
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const now = useTick(true, 60_000);

  const attachments = state.attachments[cardId];

  async function onPick(files: FileList | null): Promise<void> {
    const file = files?.[0];
    if (file === undefined) return;

    if (file.size > MAX_UPLOAD_BYTES) {
      setLocalError(`${file.name} is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_UPLOAD_BYTES)}.`);
      return;
    }

    setLocalError(null);
    setUploading(true);
    try {
      await actions.uploadAttachment(cardId, file);
    } finally {
      setUploading(false);
      // Cleared so that picking the same file twice in a row fires `change`
      // again. Without this, a failed upload that the user immediately retries
      // silently does nothing.
      if (inputRef.current !== null) inputRef.current.value = '';
    }
  }

  return (
    <section aria-label="Attachments" className="flex flex-col gap-2">
      {attachments === undefined ? (
        <p className="text-xs text-ink-subtle">Loading attachments…</p>
      ) : attachments.length === 0 ? (
        <p className="text-xs text-ink-subtle">No files attached.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {attachments.map((attachment) => (
            <li key={attachment.id} className="flex items-center gap-2 rounded-md border border-border bg-canvas px-2 py-1.5">
              {attachment.contentType.startsWith('image/') ? (
                <img
                  src={mediaUrl(attachment.assetKey)}
                  alt=""
                  loading="lazy"
                  className="h-9 w-9 rounded object-cover"
                />
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded bg-sunken text-ink-subtle">
                  <Icon name="attachment" size={14} />
                </span>
              )}

              <div className="min-w-0 flex-1">
                <a
                  href={mediaUrl(attachment.assetKey)}
                  // `download` gives the browser the stored filename; the key on
                  // the server is opaque and would otherwise become the name of
                  // the file the user saves.
                  download={attachment.filename}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-xs font-medium text-accent hover:underline"
                >
                  {attachment.filename}
                </a>
                <p className="text-[11px] text-ink-subtle">
                  {formatBytes(attachment.byteSize)} · {relativeTime(attachment.createdAt, now)}
                </p>
              </div>

              <button
                type="button"
                onClick={() => void actions.deleteAttachment(cardId, attachment.id)}
                aria-label={`Delete ${attachment.filename}`}
                className="rounded p-1 text-ink-subtle hover:bg-danger-soft hover:text-danger"
              >
                <Icon name="trash" size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div>
        <label className="sr-only" htmlFor={`attachment-${cardId}`}>
          Attach a file
        </label>
        <input
          ref={inputRef}
          id={`attachment-${cardId}`}
          type="file"
          // No `accept` attribute. The server sniffs the bytes and decides; a
          // client-side `accept` list would only hide files the server would have
          // accepted, and it is trivially bypassed by a rename anyway.
          onChange={(event) => void onPick(event.target.files)}
          disabled={uploading}
          className="hidden"
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className={ghostButtonClass}
        >
          <Icon name="attachment" size={14} />
          {uploading ? 'Uploading…' : 'Attach a file'}
        </button>
        <p className="mt-1 text-[11px] text-ink-subtle">
          JPEG, PNG, GIF, WebP, AVIF, PDF and plain text, up to {formatBytes(MAX_UPLOAD_BYTES)}. SVG is stored as text
          because it can carry script.
        </p>
        {localError !== null && <p className="mt-1 text-[11px] text-danger">{localError}</p>}
      </div>
    </section>
  );
}
